import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";

import Row from "../components/ui/core/Row";
import Column from "../components/ui/core/Column";
import { TicketNewRequestAction } from "../components/tickets/TicketNewRequestAction";
import { TicketRefreshAction } from "../components/tickets/TicketRefreshAction";
import { TicketsTable } from "../components/tickets/TicketsTable";
import { useCapabilities } from "../contexts/permissions/useCapabilities";
import { useAppStore } from "../hooks/useAppStore";
import { useTicketStore } from "../hooks/useTicketStore";
import { TicketsSearchInput } from "../components/tickets/TicketsSearchInput";
import ContactSupportDialog from "../components/ui/header/ContactSupportDialog";
import TicketDetailPanel from "../components/tickets/TicketDetailPanel";
import { useOrgLinkBranding } from "../contexts/AuthContext";

type ActivePanel =
  | { type: "newRequest" }
  | { type: "ticketDetail"; ticketId: number }
  | null;

const TicketsPage: React.FC = observer(() => {
  const theme = useTheme();
  const { sidebarWidth } = useAppStore();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showSidebar } = useCapabilities();
  const { whitelabel } = useOrgLinkBranding();
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const ticketStore = useTicketStore();

  useEffect(() => {
    ticketStore.setLiveViewing(true);
    return () => ticketStore.setLiveViewing(false);
  }, [ticketStore]);

  // Calculate available width (viewport - sidebar)
  const availableWidth =
    isMobile || !showSidebar ? "100%" : `calc(100vw - ${sidebarWidth}px)`;

  return (
    <Row
      id="tickets-page-container"
      sx={{ height: "100%", width: availableWidth, overflow: "hidden" }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "secondary.main",
          px: 4,
          py: 3,
        }}
      >
        <Row
          sx={{
            height: "auto",
            flexShrink: 0,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Column sx={{ width: "auto", height: "auto" }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "text.primary" }}
            >
              Support
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mt: 0.5 }}
            >
              Send a request to {whitelabel ? "our support team" : "the Reve team"}{" "}
              and follow every reply in one place.
            </Typography>
          </Column>

          <Row
            sx={{ width: "auto", height: "auto", gap: 1, alignItems: "center" }}
          >
            <TicketRefreshAction />
            <TicketNewRequestAction
              onClick={() => setActivePanel({ type: "newRequest" })}
            />
          </Row>
        </Row>

        <TicketsSearchInput />

        <TicketsTable
          onSelectTicket={(ticketId) =>
            setActivePanel({ type: "ticketDetail", ticketId })
          }
          selectedTicketId={
            activePanel?.type === "ticketDetail" ? activePanel.ticketId : null
          }
        />
      </Box>

      <ContactSupportDialog
        isOpen={activePanel?.type === "newRequest"}
        onClose={() => setActivePanel(null)}
      />

      <TicketDetailPanel
        ticketId={
          activePanel?.type === "ticketDetail" ? activePanel.ticketId : null
        }
        onClose={() => setActivePanel(null)}
      />
    </Row>
  );
});

export default TicketsPage;
